import * as Operators from 'customer-data-filters-ui/filterQueryFormat/operator/Operators';
import * as PropertyTypes from 'customer-data-objects/property/PropertyTypes';
import { PAGE_VIEW } from './DSAssetFamilies/DSAssetFamilies';
import { isSafe } from 'customer-data-filters-ui/utils/maxSafeInteger';
import FilterOperatorErrorRecord from './FilterOperatorErrorRecord';
import FormattedMessage from 'I18n/components/FormattedMessage';
import MissingField from './MissingField';
import get from 'transmute/get';
import getIn from 'transmute/getIn';
import { isRegexTooLong, isValidRegex, isRegexGroupCountValid } from './operator/OperatorValidators';
import { UNFORMATTED } from 'customer-data-objects/property/NumberDisplayHint';
import { DISALLOWED_URL_PARAMETERS, hasParameters, hasUtmParameterValues } from '../utils/urlUtils';
import { EVENT_BASED_FIELD_KEYS } from '../strategy/eventBasedFilters';
import { isExternalOptionsEnum } from '../utils/isExternalOptionsEnum';
import { isExternalOptionsField } from '../utils/isExternalOptionsField';
import { isRelativeTimeProperty } from '../utils/isRelativeTimeProperty';
import { jsx as _jsx } from "react/jsx-runtime";
const getInvalidUrlCharacterRegex = () => /[\s\t\r\n]+/g;
const protocolRegex = /^((http|https):\/\/)/;
const validateUrl = operator => {
  const url = operator.get('value');
  const [__protocol, domain] = protocolRegex.test(url) ? url.split('://') : [undefined, undefined];
  if (url && !protocolRegex.test(url)) {
    return FilterOperatorErrorRecord({
      error: true,
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.urlProtocol"
      })
    });
  }
  if (!domain) {
    return FilterOperatorErrorRecord({
      error: true,
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.valueRequired"
      })
    });
  }
  if (getInvalidUrlCharacterRegex().test(url)) {
    return FilterOperatorErrorRecord({
      error: true,
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.invalidUrlCharacter"
      })
    });
  }
  return {
    error: false
  };
};
const validateStandardUrl = operator => {
  const validationResult = validateUrl(operator);
  const {
    error
  } = validationResult;
  if (error) {
    return validationResult;
  }
  const urlString = operator.get('value');
  let url;
  try {
    url = new URL(urlString);
    // this is a catch-all for all invalid URL's that have not been caught by any other validation
  } catch (err) {
    if (err instanceof TypeError) {
      return {
        error: true,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.invalidUrl"
        })
      };
    } else {
      throw err;
    }
  }
  for (const [key, value] of url.searchParams) {
    if (DISALLOWED_URL_PARAMETERS.includes(key) && value) {
      return FilterOperatorErrorRecord({
        error: true,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.unsupportedUrlParameters"
        })
      });
    }
  }
  return validationResult;
};
const validateUtmUrl = operator => {
  const validationResult = validateUrl(operator);
  const {
    error
  } = validationResult;
  if (error) {
    return validationResult;
  }
  const utmBase = operator.get('utmBase');
  const [__protocol, domain] = protocolRegex.test(utmBase) ? utmBase.split('://') : [undefined, undefined];
  if (!domain) {
    return FilterOperatorErrorRecord({
      error: true,
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.valueRequired"
      })
    });
  }
  if (hasParameters(utmBase)) {
    return FilterOperatorErrorRecord({
      error: true,
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.parametersInUtm"
      })
    });
  }
  if (!hasUtmParameterValues(operator.get('utmParameters'))) {
    return FilterOperatorErrorRecord({
      error: true,
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.invalidUtmUrl"
      })
    });
  }
  return validationResult;
};
const validateMinOccurrences = operator => {
  const operatorValue = operator.get('value');
  return FilterOperatorErrorRecord({
    error: typeof operatorValue === 'number' && operatorValue <= 0,
    message: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.minOccurrences"
    }),
    opts: {
      isRefinement: true,
      preventDefault: true
    }
  });
};
const validateOperator = (operator, filterFamily, getIsUngated) => {
  const field = get('field', operator);
  const operatorValue = operator.get('value');
  const operatorHighValue = operator.get('highValue');
  const fieldType = isExternalOptionsEnum(field, getIsUngated) && isExternalOptionsField(field, filterFamily, getIsUngated) ? PropertyTypes.ENUMERATION : getIn(['field', 'type'], operator);
  if (MissingField.isMissingField(field)) {
    return FilterOperatorErrorRecord({
      error: false
    });
  }
  if ([Operators.PageViewMatchRegex, Operators.PageViewNotMatchRegex].includes(operator.constructor)) {
    if (!isValidRegex(operatorValue)) {
      return FilterOperatorErrorRecord({
        error: true,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.regexInvalid"
        })
      });
    }
    if (isRegexTooLong(operatorValue)) {
      return FilterOperatorErrorRecord({
        error: true,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.regexTooLong"
        })
      });
    }
    if (!isRegexGroupCountValid(operatorValue)) {
      return FilterOperatorErrorRecord({
        error: true,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.regexTooManyGroups"
        })
      });
    }
  }
  if (field.refinement) {
    if (fieldType === PropertyTypes.NUMBER && getIn(['field', 'name'], operator) === EVENT_BASED_FIELD_KEYS.EVENT_COUNT && !(operator instanceof Operators.UpdatedAfter) && !(operator instanceof Operators.UpdatedBefore)) {
      return validateMinOccurrences(operator);
    }
    if (fieldType === PAGE_VIEW && operator instanceof Operators.LessThanRolling && get('direction', operator) === 'forward') {
      return FilterOperatorErrorRecord({
        error: true,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.forwardRollingInvalid"
        }),
        opts: {
          isRefinement: true,
          preventDefault: true
        }
      });
    }
  }
  const isUtmUrl = operator.enableTracking;
  if (operator instanceof Operators.PageViewEqual || operator instanceof Operators.PageViewNotEqual) {
    if (isUtmUrl) {
      return validateUtmUrl(operator);
    }
    return validateStandardUrl(operator);
  }
  if (fieldType === PropertyTypes.NUMBER && !(operator instanceof Operators.UpdatedAfter) && !(operator instanceof Operators.UpdatedBefore)) {
    if (operatorValue && !isSafe(operatorValue)) {
      return FilterOperatorErrorRecord({
        error: true,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.largeNumberInvalid"
        })
      });
    }
  }
  if (fieldType === PropertyTypes.NUMBER && getIn(['field', 'numberDisplayHint'], operator) === UNFORMATTED && !(operator instanceof Operators.UpdatedAfter) && !(operator instanceof Operators.UpdatedBefore)) {
    if (operatorValue && operatorValue % 1 !== 0) {
      return FilterOperatorErrorRecord({
        error: true,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.decimalInvalid"
        })
      });
    }
  }
  if (isRelativeTimeProperty(field)) {
    if (operatorValue && !isSafe(operatorValue)) {
      return FilterOperatorErrorRecord({
        error: true,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.largeNumberInvalid"
        })
      });
    }
  }
  if (fieldType === PropertyTypes.NUMBER && operator instanceof Operators.InRange) {
    if (operatorValue && operatorHighValue && operatorValue > operatorHighValue) {
      return FilterOperatorErrorRecord({
        error: true,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.customerDataFilters.FilterEditor.operatorErrors.invalidNumericalRange"
        })
      });
    }
  }
  return FilterOperatorErrorRecord({
    error: false
  });
};
export default validateOperator;